import { useQuery } from '@apollo/client/react';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { GetFeatureFlagsDocument } from '@/graphql';
import { useAuth } from '@/features/auth';
import { FEATURE_FLAG_KEYS } from '../feature-flag-keys';

interface FeatureFlagContextValue {
  /** Check if a boolean flag is enabled. Returns false if not found or not loaded. */
  isEnabled: (key: string) => boolean;
  /** Get variant value for a variant flag. Returns null if not found or no match. */
  getVariant: (key: string) => string | null;
  /** Whether flags are currently loading */
  isLoading: boolean;
  /** Whether flags have been loaded at least once */
  isReady: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const skip = !isAuthenticated || FEATURE_FLAG_KEYS.length === 0;

  const { data, loading } = useQuery(GetFeatureFlagsDocument, {
    variables: { keys: FEATURE_FLAG_KEYS },
    skip,
    fetchPolicy: 'network-only'
  });

  const flagMap = useMemo(() => {
    const map = new Map<string, { enabled: boolean; variant?: string | null }>();
    if (data?.featureFlags) {
      for (const flag of data.featureFlags) {
        map.set(flag.key, { enabled: flag.enabled, variant: flag.variant });
      }
    }
    return map;
  }, [data]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      isEnabled: (key: string) => flagMap.get(key)?.enabled ?? false,
      isLoading: loading,
      isReady: skip || (!loading && data !== undefined),
      getVariant: (key: string) => flagMap.get(key)?.variant ?? null
    }),
    [flagMap, loading, data, skip]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Hook to access feature flags.
 *
 * @example
 * ```tsx
 * const { isEnabled } = useFeatureFlags();
 * if (isEnabled(FeatureFlagKey.PAYMENTS)) { ... }
 * ```
 */
export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

/**
 * Convenience hook for checking a single boolean flag.
 *
 * @example
 * ```tsx
 * const isPaymentsEnabled = useFeatureFlag(FeatureFlagKey.PAYMENTS);
 * ```
 */
export function useFeatureFlag(key: string): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
}
